import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { ListSkeleton } from '../pagekit';
import { callPlatformAdmin } from './platformAdminApi';
import { formatAge, formatDate, Mono, ScrollTable, TIER_META, TierChip, ToneChip } from './consoleUi';

/**
 * Right-hand drawer with one tenant's users, roles and tiers.
 * @param {object} props - Props.
 * @param {object|null} props.tenant - Tenant row from the Tenants tab (null closes the drawer).
 * @param {() => void} props.onClose - Close handler.
 * @returns {JSX.Element} Drawer.
 */
export default function TenantDrawer({ tenant, onClose }) {
  const [state, setState] = useState({ status: 'idle', users: [], error: null });
  const [attempt, setAttempt] = useState(0);
  const tenantId = tenant?.id || null;

  useEffect(() => {
    if (!tenantId) return undefined;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let live = true;
    setState({ status: 'loading', users: [], error: null });
    callPlatformAdmin('tenant_users', { accountId: tenantId }, { signal: controller?.signal }).then(
      (reply) => {
        if (live) setState({ status: 'ready', users: Array.isArray(reply?.users) ? reply.users : [], error: null });
      },
      (error) => {
        if (live && error?.name !== 'AbortError') setState({ status: 'error', users: [], error: error?.message || 'Could not load users.' });
      }
    );
    return () => {
      live = false;
      controller?.abort();
    };
  }, [tenantId, attempt]);

  const open = Boolean(tenant);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 620 }, maxWidth: '100%', bgcolor: 'background.default', boxSizing: 'border-box' },
        'aria-labelledby': 'console-tenant-title',
      }}
    >
      {tenant && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id="console-tenant-title" component="h2" sx={{ fontSize: 20, fontWeight: 650, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>
                {tenant.name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                <TierChip tier={tenant.tier} />
                {tenant.isPersonal && <ToneChip>Personal</ToneChip>}
                {tenant.stripe?.hasSubscription && <ToneChip tone="success">Subscribed</ToneChip>}
                {tenant.domain && (
                  <Typography component="span" sx={{ fontSize: 13, color: 'var(--q-muted, #5E5A54)' }}>
                    {tenant.domain}
                  </Typography>
                )}
              </Box>
            </Box>
            <Tooltip title="Close">
              <IconButton aria-label="Close tenant details" onClick={onClose} sx={{ mt: -0.5 }}>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content minmax(0, 1fr)',
              columnGap: 2,
              rowGap: 0.75,
              fontSize: 13,
              '& dt': { color: 'var(--q-muted, #5E5A54)', fontWeight: 600 },
              '& dd': { m: 0, minWidth: 0 },
            }}
          >
            <dt>Tenant id</dt>
            <dd><Mono>{tenant.id}</Mono></dd>
            <dt>Plans held</dt>
            <dd>{tenant.planNames?.length ? tenant.planNames.join(', ') : 'None'}</dd>
            <dt>Stripe</dt>
            <dd>
              {tenant.stripe?.hasSubscription ? 'Subscription' : tenant.stripe?.hasCustomer ? 'Customer, no subscription' : 'Not connected'}
              {tenant.stripe?.priceId ? <> (<Mono>{tenant.stripe.priceId}</Mono>)</> : null}
            </dd>
            <dt>Created</dt>
            <dd>{formatDate(tenant.createdAt, 'Unknown')}</dd>
            <dt>Last activity</dt>
            <dd>{formatDate(tenant.lastActivityAt)}</dd>
            <dt>Content</dt>
            <dd>{`${tenant.counts?.projects ?? 0} projects, ${tenant.counts?.sessions ?? 0} recordings, ${tenant.counts?.documents ?? 0} documents`}</dd>
          </Box>

          <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600 }}>
            {state.status === 'ready' ? `Users (${state.users.length})` : 'Users'}
          </Typography>

          {state.status === 'loading' && <ListSkeleton rows={4} label="Loading users" />}
          {state.status === 'error' && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>}
            >
              {state.error}
            </Alert>
          )}
          {state.status === 'ready' && state.users.length === 0 && (
            <Typography variant="body2" color="text.secondary">This tenant has no memberships.</Typography>
          )}
          {state.status === 'ready' && state.users.length > 0 && (
            <ScrollTable label={`Users of ${tenant.name}`} minWidth={720} maxHeight="60vh">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Last sign in</TableCell>
                  <TableCell align="right">Other tenants</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.users.map((user) => (
                  <TableRow key={user.membershipId}>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography component="div" noWrap title={user.email || ''} sx={{ fontSize: 13, fontWeight: 500 }}>
                        {user.email || 'No email'}
                      </Typography>
                      <Typography component="div" noWrap sx={{ fontSize: 12, color: 'var(--q-faint, #716C65)' }}>
                        {user.username || 'No username'}
                        {!user.emailConfirmed && !user.authMissing ? ' · unconfirmed' : ''}
                        {user.authMissing ? ' · no auth user' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{String(user.role || 'none').replace(/_/g, ' ')}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <TierChip tier={user.tier} />
                        {user.planName && user.planName !== TIER_META[user.tier]?.label && (
                          <Typography component="span" sx={{ fontSize: 12, color: 'var(--q-faint, #716C65)', whiteSpace: 'nowrap' }}>
                            {user.planName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <ToneChip tone={user.status === 'active' ? 'success' : 'neutral'}>{user.status || 'unknown'}</ToneChip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(user.joinedAt, 'Unknown')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} title={formatDate(user.lastSignInAt)}>{formatAge(user.lastSignInAt)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{user.otherTenants || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>
          )}
        </Box>
      )}
    </Drawer>
  );
}
